import { useTranslation } from 'react-i18next'
import type { LeadContactInput } from '@/api/leads'
import { Input } from '@/components/ui/input'
import { Field } from '@/components/layout/Field'
import { SalutationSelect } from '@/components/leads/SalutationSelect'

/**
 * One person on a lead. The same set of fields wherever a contact is edited.
 *
 * The lead form offers exactly one of these and no way to add a second — a lead is the
 * conversation that was had, and that conversation was with somebody. A lead that arrived carrying
 * several, from the iOS app or an import, still renders one of these per person.
 *
 * Only the surname is required. A card often carries just "A. Meyer", and refusing to store that
 * would mean losing it; the surname is what every form of address needs.
 */
export function ContactFields({
  prefix,
  contact,
  lastNameError,
  onChange,
}: {
  /** Namespaces the element ids, so several of these on one form keep their labels. */
  prefix: string
  contact: LeadContactInput
  lastNameError?: string
  onChange: (contact: LeadContactInput) => void
}) {
  const { t } = useTranslation()

  const set = <K extends keyof LeadContactInput>(key: K, value: LeadContactInput[K]) =>
    onChange({ ...contact, [key]: value })

  const text = (key: 'firstName' | 'position' | 'email' | 'phone' | 'mobile') => ({
    id: `${prefix}-${key}`,
    value: contact[key] ?? '',
    // Empty is absent, not an empty string: the backend trims to null, and sending "" would make
    // the value come back different from what was typed.
    onChange: (event: { target: { value: string } }) => set(key, event.target.value || null),
  })

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Field id={`${prefix}-salutation`} label={t('leadForm.salutation')}>
        <SalutationSelect
          id={`${prefix}-salutation`}
          value={contact.salutation}
          onChange={(value) => set('salutation', value)}
        />
      </Field>
      <Field id={`${prefix}-firstName`} label={t('leadForm.firstName')}>
        <Input {...text('firstName')} />
      </Field>
      <Field
        id={`${prefix}-lastName`}
        label={t('leadForm.lastName')}
        required
        error={lastNameError}
      >
        <Input
          id={`${prefix}-lastName`}
          value={contact.lastName}
          aria-invalid={Boolean(lastNameError)}
          aria-describedby={lastNameError ? `${prefix}-lastName-error` : undefined}
          onChange={(event) => set('lastName', event.target.value)}
        />
      </Field>

      <Field id={`${prefix}-position`} label={t('leadForm.position')}>
        <Input {...text('position')} />
      </Field>
      <Field id={`${prefix}-email`} label={t('leadForm.email')}>
        <Input type="email" autoComplete="off" {...text('email')} />
      </Field>

      <Field id={`${prefix}-phone`} label={t('leadForm.phone')}>
        <Input type="tel" {...text('phone')} />
      </Field>
      <Field id={`${prefix}-mobile`} label={t('leadForm.mobile')}>
        <Input type="tel" {...text('mobile')} />
      </Field>
    </div>
  )
}
